class TrustPolicyBarMarquee extends HTMLElement {
  connectedCallback() {
    this._reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (this._reduceMotion) {
      this.setAttribute('data-marquee-ready', '');
      return;
    }

    this._track = this.querySelector('.carousel');
    if (!this._track) return;

    const speedSeconds = Number(this.dataset.speed || 45);
    const direction = (this.dataset.direction || 'right').toLowerCase();
    // scroll_setting "right" / "left" toggles horizontal direction; keep in sync with --animation-direction in trust-policy-bar.liquid.
    this._dir = direction === 'left' ? -1 : 1;
    this._durationMs = Math.max(5, speedSeconds) * 1000;

    this._anim = null;
    this._loopWidth = 0;
    this._inView = true;
    this._resizeFrame = 0;

    this._io =
      'IntersectionObserver' in window
        ? new IntersectionObserver(
            (entries) => {
              const entry = entries[0];
              this._inView = Boolean(entry?.isIntersecting);
              this._syncPlaybackState();
            },
            { root: null, threshold: 0 }
          )
        : null;
    this._io?.observe(this);

    this.addEventListener('mouseenter', () => this._pause());
    this.addEventListener('mouseleave', () => this._play());

    window.addEventListener('resize', this._onResize, { passive: true });
    document.addEventListener('visibilitychange', this._onVisibilityChange);

    this._roFrame = 0;
    this._ro =
      'ResizeObserver' in window
        ? new ResizeObserver(() => {
            // Coalesce resize + font reflow to one setup per frame (above-the-fold friendly).
            if (this._roFrame) cancelAnimationFrame(this._roFrame);
            this._roFrame = requestAnimationFrame(() => {
              this._roFrame = 0;
              this._loopWidth = 0;
              this._setupAnimation();
            });
          })
        : null;
    this._ro?.observe(this._track);

    // Fonts (Material Symbols) can shift widths after load.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        this._loopWidth = 0;
        this._setupAnimation();
      });
    }

    // Defer first measure until layout is ready.
    requestAnimationFrame(() => this._setupAnimation());
  }

  disconnectedCallback() {
    if (this._roFrame) cancelAnimationFrame(this._roFrame);
    if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);
    this._roFrame = 0;
    this._resizeFrame = 0;
    window.removeEventListener('resize', this._onResize);
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this._io?.disconnect();
    this._ro?.disconnect();
    this._destroyAnimation();
  }

  _onResize = () => {
    if (this._resizeFrame) cancelAnimationFrame(this._resizeFrame);
    this._resizeFrame = requestAnimationFrame(() => {
      this._resizeFrame = 0;
      this._loopWidth = 0;
      this._setupAnimation();
    });
  };

  _onVisibilityChange = () => {
    this._syncPlaybackState();
  };

  _measure() {
    if (!this._track) return 0;
    const boundary = this._track.querySelector('.marquee-boundary');
    if (boundary) {
      // Exact width of the first pass (includes gaps/separators exactly).
      return boundary.offsetLeft;
    }

    const total = this._track.scrollWidth;
    // Fallback: assume 2 passes and split.
    return total > 0 ? total / 2 : 0;
  }

  _getPass1Nodes() {
    if (!this._track) return [];
    const boundary = this._track.querySelector('.marquee-boundary');
    if (!boundary) return [];
    const nodes = [];
    for (const child of Array.from(this._track.children)) {
      if (child === boundary) break;
      nodes.push(child);
    }
    return nodes;
  }

  _ensureCoverage(loopWidth) {
    if (!this._track) return;
    const pass1 = this._getPass1Nodes();
    if (!pass1.length) return;

    // If a single loop is shorter than the viewport, we need extra copies
    // so the marquee never reveals blank space.
    const minTotal = loopWidth + this.clientWidth + 50; // small buffer
    let safety = 0;

    while (this._track.scrollWidth < minTotal && safety < 10) {
      const frag = document.createDocumentFragment();
      pass1.forEach((n) => frag.appendChild(n.cloneNode(true)));
      this._track.appendChild(frag);
      safety += 1;
    }
  }

  _destroyAnimation() {
    if (this._anim) {
      this._anim.cancel();
      this._anim = null;
    }
  }

  _setupAnimation() {
    if (!this.isConnected || !this._track) return;
    if (!this._track.animate) return; // WAAPI not supported

    const loopWidth = this._measure();
    if (!loopWidth) {
      requestAnimationFrame(() => this._setupAnimation());
      return;
    }

    this._ensureCoverage(loopWidth);

    // Avoid churn if width didn't change meaningfully.
    if (this._loopWidth && Math.abs(this._loopWidth - loopWidth) < 1) {
      this._syncPlaybackState();
      return;
    }

    this._loopWidth = loopWidth;
    this._destroyAnimation();

    // Snap to whole pixels to avoid sub-pixel "jitter" at loop boundary.
    const distance = Math.max(1, Math.round(this._loopWidth));
    // 0→+W pulls the track right and exposes empty space on the left; use −W→0 instead (same period, seamless loop).
    let fromX;
    let toX;
    if (this._dir > 0) {
      fromX = -distance;
      toX = 0;
    } else {
      fromX = 0;
      toX = -distance;
    }

    this._anim = this._track.animate(
      [
        { transform: `translate3d(${fromX}px, 0, 0)` },
        { transform: `translate3d(${toX}px, 0, 0)` }
      ],
      {
        duration: this._durationMs,
        iterations: Infinity,
        easing: 'linear',
        fill: 'both'
      }
    );

    this._syncPlaybackState();
    this.setAttribute('data-marquee-ready', '');
  }

  _pause() {
    if (this._anim) this._anim.pause();
  }

  _play() {
    if (this._anim) this._anim.play();
    this._syncPlaybackState();
  }

  _syncPlaybackState() {
    if (!this._anim) return;

    // Pause if tab hidden or element out of view.
    const shouldRun = document.visibilityState === 'visible' && this._inView;
    if (shouldRun) {
      if (this._anim.playState !== 'running') this._anim.play();
    } else {
      if (this._anim.playState === 'running') this._anim.pause();
    }
  }
}

if (!customElements.get('trust-policy-bar-marquee')) {
  customElements.define('trust-policy-bar-marquee', TrustPolicyBarMarquee);
}

